/* Reactive game state. Signals own truth; components subscribe.
   The cursor lives on the LEFT half of the palindrome and ranges over
   [0, cursorMax]. Typing writes the same letter at cursor and its mirror,
   then advances. Backspace deletes the letter behind the cursor.

   The current puzzle is itself a signal — `newGame()` picks a fresh one
   from PUZZLES at random, so every "Play Again" is a different sentence. */

import { batch, computed, signal } from "@preact/signals";
import {
  buildRevealSlots,
  changedLetterIndices,
  prefersReducedMotion,
  revealTotalMs,
} from "../effects/reveal";
import {
  applyPlaythrough,
  buildPlaythrough,
  loadPlaythrough,
  savePlaythrough,
} from "./playthrough";
import { type Puzzle, analyzePuzzle, mirrorIndex } from "./puzzle";
import { PUZZLES, pickRandomPuzzle } from "./puzzles";
import { type Mark, aggregateKeyboard, scoreGuess } from "./scoring";

export const HINT_COST = 2;

export type GameStatus = "playing" | "won" | "lost";
export type GuessRow = readonly (string | null)[];

const currentPuzzle = signal<Puzzle>(pickRandomPuzzle());
export const puzzle = computed(() => currentPuzzle.value);
export const analysis = computed(() => analyzePuzzle(currentPuzzle.value));

/** Format a puzzle number with at least one leading zero, padded so the
   total width is one digit wider than the natural-digit count and never
   fewer than three: 1 → "001", 9 → "009", 10 → "010", 100 → "0100",
   500 → "0500". Used in the toolbar status line and the share-grid
   header so the puzzle id reads as a stable canonical identifier. */
export function formatPuzzleNumber(n: number): string {
  const natural = String(n);
  const width = Math.max(3, natural.length + 1);
  return natural.padStart(width, "0");
}

export const puzzleNumber = computed(() =>
  formatPuzzleNumber(currentPuzzle.value.id),
);

const blankBuffer = (): (string | null)[] =>
  new Array(analysis.value.letterCount).fill(null);

export const guesses = signal<GuessRow[]>([]);
export const buffer = signal<(string | null)[]>(blankBuffer());
export const cursor = signal<number>(0);
/** guesses.length at the moment the hint was purchased — i.e., the slot
   the player was about to enter. The two paid rows occupy that slot and
   the next, pushing later guesses down so a row stays in the visual line
   where it was typed. null means the hint has not been purchased. */
export const hintPurchasedAt = signal<number | null>(null);
export const hintRevealed = computed(() => hintPurchasedAt.value != null);
export const status = signal<GameStatus>("playing");

export const guessesUsed = computed(
  () => guesses.value.length + (hintRevealed.value ? HINT_COST : 0),
);
export const guessesRemaining = computed(
  () => puzzle.value.maxGuesses - guessesUsed.value,
);

/** Hint can be bought only if at least one guess slot remains after the purchase
   beyond the active row — i.e., remaining > HINT_COST + 1. With maxGuesses=6
   that means the latest valid purchase is during the entry of guess 3, and the
   button becomes disabled once that guess is submitted. */
export const canBuyHint = computed(
  () =>
    status.value === "playing" &&
    !hintRevealed.value &&
    guessesRemaining.value > HINT_COST + 1,
);

export const marksByGuess = computed<Mark[][]>(() => {
  const sol = analysis.value.normalized.split("");
  return guesses.value.map((g) => scoreGuess(g, sol));
});

export const keyboardMarks = computed(() =>
  aggregateKeyboard(guesses.value, marksByGuess.value),
);

/** Click-the-masthead trigger: every increment runs the full win-celebration
   sequence (mouth fire-breath + viewport confetti) without touching game
   state. Useful for previewing the effects and as a small easter egg. */
export const celebrationTrigger = signal(0);
export function fireCelebration(): void {
  celebrationTrigger.value = celebrationTrigger.value + 1;
}

/** Index into `guesses` of the row currently running its outside-in
   flip-reveal animation, or null. `submit()` sets this when a guess is
   appended; `endReveal()` clears it once the per-cell flip has settled.
   While set:
     • The active row below the just-submitted row is not rendered.
     • Letter input, cursor moves, hint purchase, and resubmission are
       all gated off — the player can't enter their next guess until the
       reveal is in.
     • The status flip (win/lose) is also deferred to `endReveal()` so
       the end-screen and celebration land together, after the reveal,
       not on top of it. */
export const revealingGuessIndex = signal<number | null>(null);
export const isRevealing = computed(() => revealingGuessIndex.value != null);

/** Letter indices that turned green for the first time on the most-recent
   submitted guess. Empty when no guesses have been submitted, or when the
   latest guess didn't reveal any new greens. The "celebration trigger" for
   per-cell confetti — emit a fresh array on every submit so subscribers
   see a value change even if the indices happen to match. */
export const newGreensInLastGuess = computed<readonly number[]>(() => {
  const all = marksByGuess.value;
  if (all.length === 0) return [];
  const last = all[all.length - 1]!;
  const out: number[] = [];
  for (let i = 0; i < last.length; i++) {
    if (last[i] !== "green") continue;
    let wasAlreadyGreen = false;
    for (let g = 0; g < all.length - 1; g++) {
      if (all[g]![i] === "green") {
        wasAlreadyGreen = true;
        break;
      }
    }
    if (!wasAlreadyGreen) out.push(i);
  }
  return out;
});

/** Single source of truth for the status summary shown both below the
   "Reveal Hint" button and at the bottom of the Share Grid. Already
   uppercase so callers can render it verbatim:

     X OF Y GUESSES LEFT • HINT UNUSED         (playing, no hint)
     X OF Y GUESSES LEFT • HINT USED AT N      (playing, hint bought)
     SOLVED IN X OF Y GUESSES • HINT UNUSED    (won, no hint)
     SOLVED IN X OF Y GUESSES • HINT USED AT N (won, hint bought)
     UNSOLVED IN Y GUESSES • HINT UNUSED       (lost, no hint)
     UNSOLVED IN Y GUESSES • HINT USED AT N    (lost, hint bought)

   N is 1-based: the guess slot the player was entering at purchase. */
export const statusLine = computed(() => {
  const total = puzzle.value.maxGuesses;
  const used = guessesUsed.value;
  const remaining = guessesRemaining.value;
  const purchased = hintPurchasedAt.value;
  const hintPart =
    purchased == null ? "HINT UNUSED" : `HINT USED AT ${purchased + 1}`;
  let countPart: string;
  if (status.value === "won") {
    countPart = `SOLVED IN ${used} OF ${total} GUESSES`;
  } else if (status.value === "lost") {
    countPart = `UNSOLVED IN ${total} GUESSES`;
  } else {
    countPart = `${remaining} OF ${total} GUESSES LEFT`;
  }
  return `${countPart} • ${hintPart}`;
});

export function typeLetter(rawCh: string): void {
  if (status.value !== "playing") return;
  if (isRevealing.value) return;
  const ch = rawCh.toLowerCase();
  if (ch.length !== 1 || !/[a-z]/.test(ch)) return;

  const a = analysis.value;
  const buf = buffer.value.slice();
  const cur = cursor.value;
  const mirror = mirrorIndex(cur, a.letterCount);
  buf[cur] = ch;
  buf[mirror] = ch;
  buffer.value = buf;
  if (cur < a.cursorMax) cursor.value = cur + 1;
}

export function backspace(): void {
  if (status.value !== "playing") return;
  if (isRevealing.value) return;
  const a = analysis.value;
  const cur = cursor.value;
  const buf = buffer.value.slice();

  if (buf[cur] != null) {
    // Clear current pair without moving cursor (allows in-place replacement).
    buf[cur] = null;
    buf[mirrorIndex(cur, a.letterCount)] = null;
    buffer.value = buf;
    return;
  }
  // Already empty — step left, then clear.
  if (cur > 0) {
    const next = cur - 1;
    buf[next] = null;
    buf[mirrorIndex(next, a.letterCount)] = null;
    buffer.value = buf;
    cursor.value = next;
  }
}

export function moveCursor(dir: -1 | 1): void {
  if (status.value !== "playing") return;
  if (isRevealing.value) return;
  const cur = cursor.value;
  const next = cur + dir;
  if (next < 0 || next > analysis.value.cursorMax) return;
  cursor.value = next;
}

/** Pending end-of-reveal timer. Tracked so swap/reset can cancel it
   before the deferred carry-forward runs against a fresh game. */
let revealTimer: number | null = null;
function cancelRevealTimer(): void {
  if (revealTimer != null) {
    clearTimeout(revealTimer);
    revealTimer = null;
  }
}

export function submit(): void {
  if (status.value !== "playing") return;
  if (isRevealing.value) return;
  // Empty cells are allowed — the player learns the hard way that skipping
  // costs a guess. They get "empty" feedback at those positions.
  const guess: GuessRow = buffer.value.slice();
  const previous =
    guesses.value.length > 0
      ? guesses.value[guesses.value.length - 1]!
      : null;
  guesses.value = [...guesses.value, guess];
  const idx = guesses.value.length - 1;
  revealingGuessIndex.value = idx;

  // Schedule the end-of-reveal commit. Until it fires, the active row
  // is hidden, input is gated, and status stays "playing" — so the
  // end-screen and celebration land together after the reveal, not on
  // top of it. Reduced-motion users skip the wait.
  const slotCount = buildRevealSlots(
    changedLetterIndices(guess, previous),
    analysis.value.letterCount,
  ).size;
  const wait = prefersReducedMotion() ? 0 : revealTotalMs(slotCount);
  cancelRevealTimer();
  if (wait <= 0) {
    endReveal();
  } else {
    revealTimer = window.setTimeout(() => {
      revealTimer = null;
      endReveal();
    }, wait);
  }
}

/** Apply the deferred consequences of the just-submitted guess: flip
   status to won/lost if applicable, otherwise carry forward greens
   into the next buffer and park the cursor on the first empty
   left-half slot. Always clears `revealingGuessIndex` so the active
   row appears and input resumes. Idempotent — a no-op when there's no
   pending reveal. Tests call this synchronously to skip the timer. */
export function endReveal(): void {
  if (revealingGuessIndex.value == null) return;
  cancelRevealTimer();

  const idx = revealingGuessIndex.value;
  const guess = guesses.value[idx];
  if (guess == null) {
    // Guesses got cleared (reset/swap) under us — just clear the flag.
    revealingGuessIndex.value = null;
    return;
  }

  const allFilled = guess.every((c) => c != null);
  if (allFilled && guess.join("") === analysis.value.normalized) {
    status.value = "won";
    revealingGuessIndex.value = null;
    persistFinishedGame();
    return;
  }
  if (guessesRemaining.value === 0) {
    status.value = "lost";
    revealingGuessIndex.value = null;
    persistFinishedGame();
    return;
  }
  // Carry forward every position that has ever been green: those letters are
  // provably correct (palindrome ⇒ mirror is also green automatically). The
  // player can still type over them; if they choose to, that's on them.
  const next = blankBuffer();
  const allGuesses = guesses.value;
  const allMarks = marksByGuess.value;
  for (let g = 0; g < allGuesses.length; g++) {
    const gs = allGuesses[g]!;
    const ms = allMarks[g]!;
    for (let i = 0; i < gs.length; i++) {
      if (ms[i] === "green") next[i] = gs[i] ?? null;
    }
  }
  buffer.value = next;
  // Park the cursor at the first still-empty left-half slot so the player
  // doesn't have to arrow past the pre-filled greens.
  const cursorMax = analysis.value.cursorMax;
  let firstEmpty = 0;
  for (let i = 0; i <= cursorMax; i++) {
    if (next[i] == null) { firstEmpty = i; break; }
    if (i === cursorMax) firstEmpty = 0;
  }
  cursor.value = firstEmpty;
  revealingGuessIndex.value = null;
}

export function buyHint(): void {
  if (!canBuyHint.value) return;
  if (isRevealing.value) return;
  hintPurchasedAt.value = guesses.value.length;
  if (guessesRemaining.value === 0) {
    status.value = "lost";
    persistFinishedGame();
  }
}

/** Snapshot the current game into a Playthrough and write it to
   localStorage, keyed by puzzle id. Called on win/loss commit. No-op
   when the game is still in progress. The brief excludes "abandoned"
   from persistence — the savePlaythrough writer also enforces that. */
function persistFinishedGame(): void {
  const record = buildPlaythrough({
    puzzle: currentPuzzle.value,
    analysis: analysis.value,
    guesses: guesses.value,
    hintPurchasedAt: hintPurchasedAt.value,
    status: status.value,
  });
  if (record != null) savePlaythrough(record);
}

/** True while `tryRestorePlaythrough` is mutating signals to install a
   stored finished-game state. Reactive subscribers (BoardEffects) read
   this flag to suppress side-effects that should fire only on a *fresh*
   win — the per-cell confetti pops and the win-burst celebration.
   Live-bound import: consumers see the current value, not a snapshot. */
export let isHydrating = false;

/** If a finished playthrough exists for the current puzzle, hydrate
   the signals from it so the player sees their finished game instead
   of a fresh board. Caller's responsibility to invoke after the
   puzzle has been swapped in and the per-puzzle signals reset.

   Wraps the writes in `batch()` so the effects fire exactly once with
   the final state, and toggles `isHydrating` around the batch so that
   single fire happens with the flag set — letting subscribers update
   their tracking variables while skipping celebrations. */
function tryRestorePlaythrough(): void {
  const stored = loadPlaythrough(currentPuzzle.value.id);
  if (stored == null) return;
  const applied = applyPlaythrough(stored, analysis.value);
  if (applied == null) return;
  isHydrating = true;
  try {
    batch(() => {
      guesses.value = applied.guesses;
      hintPurchasedAt.value = applied.hintPurchasedAt;
      status.value = applied.status;
    });
  } finally {
    isHydrating = false;
  }
}

/** Clear game state without changing the puzzle. */
function clearGame(): void {
  cancelRevealTimer();
  guesses.value = [];
  buffer.value = blankBuffer();
  cursor.value = 0;
  hintPurchasedAt.value = null;
  status.value = "playing";
  revealingGuessIndex.value = null;
}

/** Start a new game with a freshly-picked random puzzle. Each call gives a
   different sentence (or, if PUZZLES has only one element, the same one). */
export function newGame(): void {
  // Avoid handing back the same puzzle two games in a row when possible.
  let next = pickRandomPuzzle();
  if (PUZZLES.length > 1) {
    const cur = currentPuzzle.value;
    let guard = 8;
    while (next.id === cur.id && guard-- > 0) next = pickRandomPuzzle();
  }
  swapPuzzle(next);
}

/** Reset the current game (same puzzle). Exposed primarily for tests. */
export function reset(): void {
  clearGame();
}

/** Force a specific puzzle. Tests use this for determinism. */
export function setPuzzle(p: Puzzle): void {
  swapPuzzle(p);
}

/** Swap to a new puzzle, ordering writes so dependent computeds never
   observe a transient (new analysis × old guesses) state. marksByGuess
   would otherwise throw a length mismatch the moment we change the
   puzzle, and the throw propagates out of the call site (e.g. newGame
   from a Play Again click) before clearGame() can run. */
function swapPuzzle(p: Puzzle): void {
  cancelRevealTimer();
  // 1. Drop the old guesses (and the rest of the per-game state that
  //    doesn't depend on letter count) BEFORE the puzzle changes —
  //    so when analysis flips, marksByGuess re-scores [] against the
  //    new solution.
  guesses.value = [];
  hintPurchasedAt.value = null;
  status.value = "playing";
  revealingGuessIndex.value = null;
  // 2. Swap the puzzle.
  currentPuzzle.value = p;
  // 3. Re-initialise the letter-count-shaped state against the new
  //    analysis.
  buffer.value = blankBuffer();
  cursor.value = 0;
  // 4. If the player has already completed this puzzle in a prior
  //    session, hydrate the signals from the stored playthrough so
  //    the finished game is shown read-only — no new gameplay.
  tryRestorePlaythrough();
}

// Boot: the module-level signal init above ran pickRandomPuzzle() but
// did not consult storage. Apply any prior playthrough now so the very
// first paint shows the finished state, not a transient empty board.
tryRestorePlaythrough();
